import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, User, Shield, Eye, EyeOff } from 'lucide-react';
import { ASSETS } from '../config/assets';
import BrandLogo from '../components/BrandLogo';

const PortalPage = () => {
  const { user, loading, login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user && user.must_change_password) {
      navigate('/marcom/change-password');
    } else if (user) {
      navigate('/marcom');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.must_change_password) {
        navigate('/marcom/change-password');
      } else {
        navigate('/marcom');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-wine border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[#0D0D0D] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-block mb-6">
              <BrandLogo className="h-24 mx-auto" />
            </div>
            <h1
              data-testid="portal-title"
              className="font-heading text-2xl font-semibold text-white mb-2"
            >
              {t('portal.title')}
            </h1>
            <p className="text-white/50">
              {t('portal.subtitle')}
            </p>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 mb-6 p-3 bg-wine/10 rounded-lg border border-wine/20">
            <Shield className="w-4 h-4 text-wine" strokeWidth={1.5} />
            <span className="text-xs text-white/70 font-medium">
              {language === 'fr' ? 'Connexion Securisee' : 'Secure Login'}
            </span>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80 font-medium">
                {t('portal.emailLabel')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" strokeWidth={1.5} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('portal.emailPlaceholder')}
                  data-testid="portal-email-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg h-12"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 font-medium">
                {t('portal.passwordLabel')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" strokeWidth={1.5} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('portal.passwordPlaceholder')}
                  data-testid="portal-password-input"
                  className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg h-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-red-400 text-sm">{error}</p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="portal-login-btn"
              className="w-full bg-wine hover:bg-wine-light text-white rounded-lg h-12 text-base font-medium"
            >
              {submitting
                ? (language === 'fr' ? 'Connexion...' : 'Signing in...')
                : t('portal.loginButton')
              }
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-white/40">
              {t('portal.noAccount')}{' '}
              <Link
                to="/contact"
                className="text-wine hover:underline"
                data-testid="contact-us-link"
              >
                {t('portal.contactUs')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalPage;
